import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import QRCodeTab from './QRCodeTab';
import FAQsTab from './FAQsTab';
import DiscountsSettings from './DiscountsSettings';
import AIAssistantTab from './AIAssistantTab';
import TCsTab from './TCsTab';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'studio', label: 'Studio Info' },
  { id: 'discounts', label: 'Discounts' },
  { id: 'faqs', label: 'FAQs' },
  { id: 'ai', label: 'AI Assistant' },
  { id: 'tcs', label: 'T&Cs' },
  { id: 'qrcode', label: 'QR Code' },
];

const SettingsPage = () => {
  const { user, studio, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [message, setMessage] = useState('');

  // Profile state
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });

  // Sender Identity state (Studio Info tab)
  const [senderName, setSenderName] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  // Integration state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [zoomStatus, setZoomStatus] = useState(null);
  const [zoomLoading, setZoomLoading] = useState(true);
  const [zoomConnecting, setZoomConnecting] = useState(false);
  const [zoomDisconnecting, setZoomDisconnecting] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleDisconnecting, setGoogleDisconnecting] = useState(false);

  // Check for return from OAuth flows
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    const zoomParam = searchParams.get('zoom');
    const googleParam = searchParams.get('google');

    const tabParam = searchParams.get('tab');
    if (tabParam && TABS.some(t => t.id === tabParam)) setActiveTab(tabParam);
    else if (stripeParam || zoomParam || googleParam) setActiveTab('integrations');

    if (stripeParam === 'connected') setMessage('Stripe account connected!');
    else if (stripeParam === 'refresh') setMessage('Stripe onboarding link expired. Click below to continue.');
    if (zoomParam === 'connected') { setMessage('Zoom connected!'); fetchZoomStatus(); }
    else if (zoomParam === 'error') setMessage('Failed to connect Zoom.');
    if (googleParam === 'connected') { setMessage('Google Classroom connected!'); fetchGoogleStatus(); }
    else if (googleParam === 'error') setMessage('Failed to connect Google Classroom.');
  }, [searchParams]);

  const fetchStripeStatus = async () => { try { const { data } = await api.get('/stripe/connect/status'); setStripeStatus(data); } catch {} finally { setLoading(false); } };
  const fetchZoomStatus = async () => { try { const { data } = await api.get('/zoom/status'); setZoomStatus(data); } catch {} finally { setZoomLoading(false); } };
  const fetchGoogleStatus = async () => { try { const { data } = await api.get('/google/classroom/status'); setGoogleStatus(data); } catch {} finally { setGoogleLoading(false); } };

  useEffect(() => { fetchStripeStatus(); fetchZoomStatus(); fetchGoogleStatus(); }, []);

  // Init sender identity from studio
  useEffect(() => {
    if (studio?.emailSettings) {
      setSenderAddress(studio.emailSettings.senderAddress || '');
      setSenderName(studio.emailSettings.senderName || studio.name || '');
      setReplyTo(studio.emailSettings.replyTo || '');
    } else if (studio) {
      setSenderName(studio.name || '');
      setSenderAddress(studio.slug ? `${studio.slug}@mydancehall.com` : '');
    }
  }, [studio]);

  const handleEmailSettingsSave = async () => {
    setEmailSaving(true); setEmailSuccess('');
    try {
      await api.put('/studio', { emailSettings: { senderAddress, senderName, replyTo } });
      await refreshUser();
      setEmailSuccess('Sender identity saved');
      setTimeout(() => setEmailSuccess(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to save sender identity');
    } finally { setEmailSaving(false); }
  };

  const handleConnectStripe = async () => { setConnecting(true); try { const { data } = await api.post('/stripe/connect/onboard'); window.location.href = data.url; } catch (err) { setMessage(err.response?.data?.error || 'Failed'); setConnecting(false); } };
  const handleConnectZoom = async () => { setZoomConnecting(true); try { const { data } = await api.get('/zoom/connect'); window.location.href = data.authUrl; } catch (err) { setMessage(err.response?.data?.error || 'Failed'); setZoomConnecting(false); } };
  const handleDisconnectZoom = async () => { if (!window.confirm('Disconnect Zoom?')) return; setZoomDisconnecting(true); try { await api.post('/zoom/disconnect'); setZoomStatus({ connected: false }); setMessage('Zoom disconnected.'); } catch (err) { setMessage(err.response?.data?.error || 'Failed'); } finally { setZoomDisconnecting(false); } };
  const handleConnectGoogle = async () => { setGoogleConnecting(true); try { const { data } = await api.get('/google/connect'); window.location.href = data.authUrl; } catch (err) { setMessage(err.response?.data?.error || 'Failed'); setGoogleConnecting(false); } };
  const handleDisconnectGoogle = async () => { if (!window.confirm('Disconnect Google Classroom?')) return; setGoogleDisconnecting(true); try { await api.post('/google/disconnect'); setGoogleStatus({ connected: false }); setMessage('Google Classroom disconnected.'); } catch (err) { setMessage(err.response?.data?.error || 'Failed'); } finally { setGoogleDisconnecting(false); } };
  const handleViewDashboard = async () => { try { const { data } = await api.get('/stripe/connect/dashboard'); window.open(data.url, '_blank'); } catch { setMessage('Failed to open Stripe dashboard'); } };

  const handleProfileSave = async () => {
    setSaving(true); setProfileError('');
    try { await api.put('/auth/profile', form); await refreshUser(); setEditing(false); setProfileSuccess('Profile updated'); setTimeout(() => setProfileSuccess(''), 3000); }
    catch (err) { setProfileError(err.response?.data?.error || 'Failed to update profile'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Settings</h1>

      {/* Tabs — horizontally scrollable on mobile so adding tabs never overflows the viewport */}
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-brand-500 border-brand-500'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {message && (
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center justify-between">
          {message}
          <button onClick={() => setMessage('')} className="text-blue-500 hover:text-blue-700 ml-2">&times;</button>
        </div>
      )}

      {/* ─── Profile Tab ──────────────────────────── */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-lg">
          {profileSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{profileSuccess}</div>}
          {profileError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{profileError}</div>}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              {editing ? (
                <input type="text" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
              ) : (
                <p className="text-gray-900">{user?.name || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-gray-900">{user?.email}</p>
              <p className="text-xs text-gray-400 mt-1">Email cannot be changed here</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              {editing ? (
                <input type="tel" value={form.phone} onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Enter your phone number" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
              ) : (
                <p className="text-gray-900">{user?.phone || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <p className="text-gray-900 capitalize">{user?.role || '-'}</p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            {editing ? (
              <>
                <button onClick={handleProfileSave} disabled={saving} className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">{saving ? 'Saving...' : 'Save'}</button>
                <button onClick={() => { setEditing(false); setForm({ name: user?.name || '', phone: user?.phone || '' }); setProfileError(''); }} className="text-gray-600 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">Cancel</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors">Edit Profile</button>
            )}
          </div>
        </div>
      )}

      {/* ─── Integrations Tab ─────────────────────── */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Stripe */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Payments</h2>
            <p className="text-sm text-gray-500 mb-4">Connect Stripe to receive payments. You keep 100% — students pay a 2.99% service fee.</p>
            {loading ? <div className="text-sm text-gray-400">Checking...</div>
            : stripeStatus?.connected && stripeStatus?.status === 'active' ? (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-sm font-medium text-green-700">Stripe Connected</span></div>
                <div className="text-sm text-gray-500 space-y-1 mb-4"><div>Charges: {stripeStatus.chargesEnabled ? 'Enabled' : 'Disabled'}</div><div>Payouts: {stripeStatus.payoutsEnabled ? 'Enabled' : 'Disabled'}</div></div>
                <button onClick={handleViewDashboard} className="text-sm text-brand-500 hover:text-brand-600 font-medium">Open Stripe Dashboard</button>
              </div>
            ) : stripeStatus?.connected ? (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-sm font-medium text-yellow-700">Setup Incomplete</span></div>
                <button onClick={handleConnectStripe} disabled={connecting} className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">{connecting ? 'Redirecting...' : 'Complete Stripe Setup'}</button>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-gray-400" /><span className="text-sm font-medium text-gray-600">Not Connected</span></div>
                <button onClick={handleConnectStripe} disabled={connecting} className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">{connecting ? 'Redirecting...' : 'Connect Stripe Account'}</button>
              </div>
            )}
          </div>

          {/* Zoom */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Zoom</h2>
            <p className="text-sm text-gray-500 mb-4">Auto-create meeting links for online courses.</p>
            {zoomLoading ? <div className="text-sm text-gray-400">Checking...</div>
            : zoomStatus?.connected ? (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-sm font-medium text-green-700">Zoom Connected</span></div>
                <button onClick={handleDisconnectZoom} disabled={zoomDisconnecting} className="text-sm text-red-500 hover:text-red-700 font-medium">{zoomDisconnecting ? 'Disconnecting...' : 'Disconnect Zoom'}</button>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-gray-400" /><span className="text-sm font-medium text-gray-600">Not Connected</span></div>
                <button onClick={handleConnectZoom} disabled={zoomConnecting} className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">{zoomConnecting ? 'Redirecting...' : 'Connect Zoom'}</button>
              </div>
            )}
          </div>

          {/* Google Classroom */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Google Classroom</h2>
            <p className="text-sm text-gray-500 mb-4">Sync courses, invite students, and share materials.</p>
            {googleLoading ? <div className="text-sm text-gray-400">Checking...</div>
            : googleStatus?.connected ? (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-sm font-medium text-green-700">Google Classroom Connected</span></div>
                <button onClick={handleDisconnectGoogle} disabled={googleDisconnecting} className="text-sm text-red-500 hover:text-red-700 font-medium">{googleDisconnecting ? 'Disconnecting...' : 'Disconnect'}</button>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-gray-400" /><span className="text-sm font-medium text-gray-600">Not Connected</span></div>
                <button onClick={handleConnectGoogle} disabled={googleConnecting} className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">{googleConnecting ? 'Redirecting...' : 'Connect Google Classroom'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Studio Info Tab ──────────────────────── */}
      {activeTab === 'studio' && (
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Studio Logo</h2>
            <p className="text-sm text-gray-500 mb-4">Appears on your login portal, dashboard sidebar, and student dashboard.</p>
            <div className="flex items-center gap-6">
              {studio?.branding?.logo ? (
                <img src={studio.branding.logo} alt={studio?.name} className="w-20 h-20 rounded-xl object-contain border border-gray-200" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-brand-100 flex items-center justify-center border border-brand-200">
                  <span className="text-2xl font-bold text-brand-600">{studio?.name?.charAt(0) || 'S'}</span>
                </div>
              )}
              <div>
                <label className="inline-block bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors cursor-pointer">
                  Upload Logo
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('logo', file);
                      try {
                        await api.post('/studio/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        await refreshUser();
                        setMessage('Logo uploaded!');
                      } catch (err) {
                        setMessage(err.response?.data?.error || 'Failed to upload logo');
                      }
                    }}
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">PNG or JPG, max 5MB</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Studio Information</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="text-gray-900 font-medium">{studio?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slug</span><span className="text-gray-900 font-mono text-xs">{studio?.slug}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Programs</span><span className="text-gray-900 capitalize">{studio?.danceStyles?.join(', ')}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="text-gray-900">{[studio?.location?.city, studio?.location?.state].filter(Boolean).join(', ') || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Levels</span><span className="text-gray-900">{studio?.levels?.map(l => l.name).join(', ') || '—'}</span></div>
            </div>
          </div>

          {/* Sender Identity */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Sender Identity</h2>
            <p className="text-sm text-gray-500 mb-4">Students will see this name and address on emails from your studio.</p>
            {emailSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{emailSuccess}</div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sender Name</label>
                <input type="text" value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="Enter your studio name" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">Displayed as the "from" name in student inboxes</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email Address</label>
                <input type="text" value={senderAddress} onChange={e => setSenderAddress(e.target.value)} placeholder="yourstudio@mydancehall.com" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">Must end with @mydancehall.com</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email for student &amp; enrollment questions</label>
                <input type="email" value={replyTo} onChange={e => setReplyTo(e.target.value)} placeholder="Enter your email address" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">New-enrollment alerts and questions students send go here. Replies to your notification emails land here too.</p>
              </div>
              <div className="flex justify-end">
                <button onClick={handleEmailSettingsSave} disabled={emailSaving} className="bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">
                  {emailSaving ? 'Saving...' : 'Save Sender Identity'}
                </button>
              </div>
            </div>
          </div>

        </div>
      )}

      {activeTab === 'discounts' && <DiscountsSettings />}
      {activeTab === 'faqs' && <FAQsTab />}
      {activeTab === 'ai' && <AIAssistantTab />}
      {activeTab === 'tcs' && <TCsTab />}
      {activeTab === 'qrcode' && <QRCodeTab />}
    </div>
  );
};

export default SettingsPage;
